// RegimeChart.jsx — v9.1c (Lightweight Charts v5)
//
// Daily-Chart mit:
//   - Candles via Lightweight Charts (TradingView-Engine)
//   - Daily-Regime als HINTERGRUND-Bänder (HTML-Overlay)
//   - H1-Regime als kompakter Streifen UNTER dem Chart
//   - Shift+Drag-to-Select für H1-Range
//   - Full Zoom/Pan/Pinch wie TradingView
//
// v5 API: chart.addSeries(CandlestickSeries, {...})

import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { createChart, CandlestickSeries, LineSeries, createSeriesMarkers, LineStyle } from "lightweight-charts";
import { theme, fonts } from "../../theme";
import { API_BASE } from "../../config";

function regimeColor(phase, trend) {
  if (phase === 0) return "rgba(148, 163, 184, 0.12)";
  if (phase === 2) return "rgba(251, 146, 60, 0.22)";
  if (phase === 1) {
    if (trend === 1)  return "rgba(22, 163, 74, 0.18)";
    if (trend === -1) return "rgba(220, 38, 38, 0.22)";
  }
  return "rgba(148, 163, 184, 0.08)";
}

function regimeSolidColor(phase, trend) {
  if (phase === 0) return "#cbd5e1";
  if (phase === 2) return "#fb923c";
  if (phase === 1) {
    if (trend === 1)  return "#16a34a";
    if (trend === -1) return "#dc2626";
  }
  return "#94a3b8";
}

function regimeLabel(phase, trend) {
  if (phase === 0) return "Undefined";
  if (phase === 2) return trend === 1 ? "Pivot Bull" : trend === -1 ? "Pivot Bear" : "Pivot";
  if (phase === 1) return trend === 1 ? "ETAB Bull" : trend === -1 ? "ETAB Bear" : "ETAB neutral";
  return "?";
}

function parseISO(s) {
  if (!s) return null;
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

function buildSegments(transitions, periodEnd) {
  if (!Array.isArray(transitions) || !transitions.length) return [];
  const segs = [];
  for (let i = 0; i < transitions.length; i++) {
    const t = transitions[i];
    const start = parseISO(t.ts);
    if (!start) continue;
    const end = i + 1 < transitions.length ? parseISO(transitions[i + 1].ts) : periodEnd;
    if (!end) continue;
    segs.push({
      start, end,
      phase: t.phase, trend: t.trend,
      color: regimeColor(t.phase, t.trend),
      solidColor: regimeSolidColor(t.phase, t.trend),
      label: regimeLabel(t.phase, t.trend),
    });
  }
  return segs;
}

export default function RegimeChart({
  dailyOhlc,
  dailyTransitions,
  h1Transitions,
  runStart,
  runEnd,
  onRangeSelect,
  selectedRange,
  instrument = "MGC",
}) {
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const seriesRef = useRef(null);
  const markersApiRef = useRef(null);
  // v9.1c (Lines): Pro Signal eine LineSeries mit 2 Punkten (level_start → signal_time)
  // damit klassische SMC-Linien gezeichnet werden statt Marker-Squares.
  const signalLinesRef = useRef([]); // ISeriesApi[]
  const [chartReady, setChartReady] = useState(false);

  const [bandPositions, setBandPositions] = useState([]);
  const [redrawTrigger, setRedrawTrigger] = useState(0);

  // BoS/CHoCH Signal-Detection (auf Daily-TF)
  const [showSignals, setShowSignals] = useState(false);
  const [signals, setSignals] = useState([]);
  const [signalsLoading, setSignalsLoading] = useState(false);
  const [signalsError, setSignalsError] = useState(null);

  // v9.1c: Log-Scale für Y-Achse (default ON wegen Gold-Verlauf $1700→$5500)
  const [logScale, setLogScale] = useState(true);

  const dailySegs = useMemo(() => buildSegments(dailyTransitions, runEnd), [dailyTransitions, runEnd]);
  const h1Segs    = useMemo(() => buildSegments(h1Transitions, runEnd),    [h1Transitions, runEnd]);

  // Initialize chart
  useEffect(() => {
    if (!containerRef.current || !dailyOhlc || !dailyOhlc.length) return;

    const chart = createChart(containerRef.current, {
      width: containerRef.current.clientWidth,
      height: 440,
      layout: {
        // Transparent background damit die Regime-Bänder darunter durchscheinen
        background: { type: "solid", color: "rgba(0, 0, 0, 0)" },
        textColor: "#475569",
        fontFamily: fonts?.body || "system-ui",
      },
      grid: {
        vertLines: { color: "#f1f5f9" },
        horzLines: { color: "#f1f5f9" },
      },
      rightPriceScale: {
        borderColor: "#e2e8f0",
        // v9.1c: Logarithmische Skala falls aktiv (entspannt Y-Stauchung bei Gold-Verlauf $1700→$5500)
        mode: logScale ? 1 : 0,   // 0=Normal, 1=Log, 2=Percentage, 3=IndexedTo100
        autoScale: true,
      },
      timeScale: {
        borderColor: "#e2e8f0",
        timeVisible: false,
        secondsVisible: false,
      },
      crosshair: {
        mode: 1,
        vertLine: { color: "#94a3b8", labelBackgroundColor: "#1e293b" },
        horzLine: { color: "#94a3b8", labelBackgroundColor: "#1e293b" },
      },
    });

    const candleSeries = chart.addSeries(CandlestickSeries, {
      upColor: "#16a34a", downColor: "#dc2626",
      borderUpColor: "#16a34a", borderDownColor: "#dc2626",
      wickUpColor: "#16a34a", wickDownColor: "#dc2626",
      // v9.1c: Ganzzahl-Preise (Gold ist in vollen $)
      priceFormat: { type: "price", precision: 0, minMove: 1 },
    });

    const data = dailyOhlc
      .map(b => {
        const dt = parseISO(b.ts);
        if (!dt) return null;
        return { time: Math.floor(dt.getTime() / 1000), open: b.o, high: b.h, low: b.l, close: b.c };
      })
      .filter(Boolean)
      .sort((a, b) => a.time - b.time);

    candleSeries.setData(data);

    chartRef.current = chart;
    seriesRef.current = candleSeries;
    setChartReady(true);

    chart.timeScale().fitContent();

    const ro = new ResizeObserver(() => {
      if (containerRef.current && chartRef.current) {
        chartRef.current.applyOptions({ width: containerRef.current.clientWidth });
        setRedrawTrigger(n => n + 1);
      }
    });
    ro.observe(containerRef.current);

    return () => {
      ro.disconnect();
      chart.remove();
      chartRef.current = null;
      seriesRef.current = null;
      setChartReady(false);
    };
  }, [dailyOhlc, logScale]);

  // Update band positions on timescale change
  const updateBandPositions = useCallback(() => {
    if (!chartRef.current) return;
    const timeScale = chartRef.current.timeScale();
    const positions = dailySegs.map((seg, idx) => {
      const tStart = Math.floor(seg.start.getTime() / 1000);
      const tEnd   = Math.floor(seg.end.getTime() / 1000);
      const xStart = timeScale.timeToCoordinate(tStart);
      const xEnd   = timeScale.timeToCoordinate(tEnd);
      if (xStart === null || xEnd === null) return null;
      return {
        idx,
        left: Math.min(xStart, xEnd),
        width: Math.max(1, Math.abs(xEnd - xStart)),
        color: seg.color,
        seg,
      };
    }).filter(Boolean);
    setBandPositions(positions);
  }, [dailySegs]);

  useEffect(() => {
    if (!chartReady || !chartRef.current) return;
    updateBandPositions();
    const ts = chartRef.current.timeScale();
    const handler = () => updateBandPositions();
    ts.subscribeVisibleTimeRangeChange(handler);
    ts.subscribeVisibleLogicalRangeChange(handler);
    return () => {
      ts.unsubscribeVisibleTimeRangeChange(handler);
      ts.unsubscribeVisibleLogicalRangeChange(handler);
    };
  }, [chartReady, updateBandPositions, redrawTrigger]);

  // Fetch BoS/CHoCH Signals für Daily-TF wenn Toggle aktiv
  useEffect(() => {
    if (!showSignals || !runStart || !runEnd || !instrument) {
      setSignals([]);
      setSignalsError(null);
      return;
    }
    setSignalsLoading(true);
    setSignalsError(null);
    const base = API_BASE || "";
    const params = new URLSearchParams({
      instrument,
      start: runStart.toISOString(),
      end: runEnd.toISOString(),
      tf: "D",
      with_signals: "true",
      swing_length: "10",
      _: Date.now().toString(),   // cache-bust gegen Cloudflare/Browser-Cache
    });
    const url = `${base}/regime-detail?${params}`;
    console.log("[RegimeChart] Fetching Daily signals:", url);
    fetch(url, { cache: "no-store" })
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(d => {
        console.log("[RegimeChart] Daily signals received:", d.signals?.length || 0);
        setSignals(d.signals || []);
        setSignalsLoading(false);
      })
      .catch(e => {
        console.warn("[RegimeChart] Signal fetch failed:", e);
        setSignalsError(e.message);
        setSignalsLoading(false);
      });
  }, [showSignals, runStart?.getTime(), runEnd?.getTime(), instrument]);

  // Render BoS/CHoCH als horizontale Linien (Pivot-Level → Breakout)
  useEffect(() => {
    if (!chartReady || !chartRef.current) return;
    // Cleanup previous lines
    for (const ls of signalLinesRef.current) {
      try { chartRef.current.removeSeries(ls); } catch {}
    }
    signalLinesRef.current = [];

    // Clear any existing markers (we don't use them for signals anymore)
    if (markersApiRef.current) {
      try { markersApiRef.current.setMarkers([]); } catch {}
    }

    if (!showSignals || signals.length === 0) return;

    // Letzten Candle-Zeitpunkt ermitteln, damit Linien die danach enden geclippt werden
    // (Lightweight Charts zeichnet keine LineSeries-Punkte außerhalb der Candle-Range)
    let lastCandleTs = 0;
    for (const b of dailyOhlc) {
      const dt = parseISO(b.ts);
      if (dt) {
        const ts = Math.floor(dt.getTime() / 1000);
        if (ts > lastCandleTs) lastCandleTs = ts;
      }
    }

    let added = 0;
    let clippedCount = 0;
    for (const s of signals) {
      const startDate = new Date(s.level_start);
      const endDate = new Date(s.time);
      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) continue;
      const t0 = Math.floor(startDate.getTime() / 1000);
      let t1 = Math.floor(endDate.getTime() / 1000);
      // Clip auf letzten Candle wenn t1 darüber liegt
      if (lastCandleTs > 0 && t1 > lastCandleTs) {
        t1 = lastCandleTs;
        clippedCount++;
      }
      if (t1 <= t0) continue;

      const color = s.type === "CHoCH" ? "#2563eb" : "#7c3aed";
      try {
        const line = chartRef.current.addSeries(LineSeries, {
          color,
          lineWidth: 1,
          lineStyle: LineStyle.Dashed,
          lastValueVisible: false,
          priceLineVisible: false,
          crosshairMarkerVisible: false,
          priceScaleId: "right",
        });
        const lineData = [
          { time: t0, value: s.price },
          { time: t1, value: s.price },
        ];
        line.setData(lineData);
        if (added === 0) {
          console.log("[RegimeChart] First signal line data:", lineData, "color:", color);
        }
        // Speichere Signal-Meta auf der Series für Label-Positionierung
        line._sigMeta = { t0, t1, price: s.price, color, label: s.type };
        signalLinesRef.current.push(line);
        added++;
      } catch (e) {
        console.warn("[RegimeChart] Could not add signal line:", e);
      }
    }
    console.log("[RegimeChart] Rendered", added, "Daily signal lines (clipped:", clippedCount, ")");
    // Trigger label position recompute
    setLabelTrigger(n => n + 1);
  }, [chartReady, signals, showSignals, dailyOhlc]);

  // Labels mittig auf den Linien als HTML-Overlay (kein Chart-Marker)
  const [labelTrigger, setLabelTrigger] = useState(0);
  const [signalLabels, setSignalLabels] = useState([]);

  const updateSignalLabels = useCallback(() => {
    if (!chartRef.current || !seriesRef.current || signalLinesRef.current.length === 0) {
      setSignalLabels([]);
      return;
    }
    const timeScale = chartRef.current.timeScale();
    const series = seriesRef.current;
    const labels = [];
    for (const line of signalLinesRef.current) {
      const meta = line._sigMeta;
      if (!meta) continue;
      // Y-Koordinate: nur vom Preis abhängig, immer verfügbar
      const y = series.priceToCoordinate(meta.price);
      if (y === null) continue;
      // X-Koordinate: versuche tMid, dann tStart, dann tEnd
      // (manche Mid-Zeitpunkte fallen auf Samstage/Feiertage = null bei Daily-TF)
      const tMid = Math.floor((meta.t0 + meta.t1) / 2);
      let xMid = timeScale.timeToCoordinate(tMid);
      let xStart = timeScale.timeToCoordinate(meta.t0);
      let xEnd = timeScale.timeToCoordinate(meta.t1);
      let x;
      if (xMid !== null) {
        x = xMid;
      } else if (xStart !== null && xEnd !== null) {
        // Fallback: berechne Mitte aus den Endpunkten
        x = (xStart + xEnd) / 2;
      } else if (xStart !== null) {
        x = xStart;
      } else if (xEnd !== null) {
        x = xEnd;
      } else {
        continue; // Linie komplett außerhalb des sichtbaren Bereichs
      }
      labels.push({ x, y, text: meta.label, color: meta.color });
    }
    setSignalLabels(labels);
  }, []);

  useEffect(() => {
    if (!chartReady || !chartRef.current) return;
    updateSignalLabels();
    const ts = chartRef.current.timeScale();
    const handler = () => updateSignalLabels();
    ts.subscribeVisibleTimeRangeChange(handler);
    ts.subscribeVisibleLogicalRangeChange(handler);
    return () => {
      ts.unsubscribeVisibleTimeRangeChange(handler);
      ts.unsubscribeVisibleLogicalRangeChange(handler);
    };
  }, [chartReady, updateSignalLabels, labelTrigger, redrawTrigger]);

  // ─── Range-Auswahl: Modus + Two-Click Pattern ───
  // UX: Klick auf "Range wählen" Button aktiviert Selection-Modus.
  // Im Selection-Modus: 1. Klick = Start, 2. Klick = Ende, dann Modus deaktiv.
  // ESC bricht ab. Wir nutzen chart.subscribeClick() statt eigene Mouse-Handler
  // damit es nicht mit Lightweight Charts internal Pan kollidiert.
  const [selectionMode, setSelectionMode] = useState(false);
  const [pendingStart, setPendingStart] = useState(null); // Date | null

  // Click-Handler über Lightweight Charts API (kein Konflikt mit Pan)
  useEffect(() => {
    if (!chartReady || !chartRef.current) return;
    const chart = chartRef.current;
    const onClick = (param) => {
      if (!selectionMode) return;
      if (!param || !param.time) return;
      const t = Number(param.time);
      const clickedDate = new Date(t * 1000);
      if (pendingStart === null) {
        // 1. Klick: Start setzen
        setPendingStart(clickedDate);
      } else {
        // 2. Klick: Ende setzen, Range submit, Modus aus
        const s = pendingStart < clickedDate ? pendingStart : clickedDate;
        const e = pendingStart < clickedDate ? clickedDate : pendingStart;
        if (e - s > 1000 * 60 * 60 * 24 && onRangeSelect) {
          onRangeSelect(s, e);
        }
        setPendingStart(null);
        setSelectionMode(false);
      }
    };
    chart.subscribeClick(onClick);
    return () => chart.unsubscribeClick(onClick);
  }, [chartReady, selectionMode, pendingStart, onRangeSelect]);

  // ESC bricht ab
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        setSelectionMode(false);
        setPendingStart(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Selection overlay: zeigt aktuelle Auswahl als blau gefüllten Bereich
  const selectionCoords = useMemo(() => {
    if (!selectedRange || !chartRef.current) return null;
    const ts = chartRef.current.timeScale();
    const xStart = ts.timeToCoordinate(Math.floor(selectedRange[0].getTime() / 1000));
    const xEnd   = ts.timeToCoordinate(Math.floor(selectedRange[1].getTime() / 1000));
    if (xStart === null || xEnd === null) return null;
    return { left: Math.min(xStart, xEnd), width: Math.max(1, Math.abs(xEnd - xStart)) };
  }, [selectedRange, bandPositions]);

  // Pending-Start-Marker (während Selection-Modus zwischen 1. und 2. Klick)
  const pendingStartCoord = useMemo(() => {
    if (!pendingStart || !chartRef.current) return null;
    const ts = chartRef.current.timeScale();
    const x = ts.timeToCoordinate(Math.floor(pendingStart.getTime() / 1000));
    return x === null ? null : x;
  }, [pendingStart, bandPositions]);

  // H1-Strip positions (depends on bandPositions)
  const h1StripPositions = useMemo(() => {
    if (!chartRef.current) return [];
    const ts = chartRef.current.timeScale();
    return h1Segs.map((seg, idx) => {
      const tStart = Math.floor(seg.start.getTime() / 1000);
      const tEnd   = Math.floor(seg.end.getTime() / 1000);
      const xStart = ts.timeToCoordinate(tStart);
      const xEnd   = ts.timeToCoordinate(tEnd);
      if (xStart === null || xEnd === null) return null;
      return {
        idx,
        left: Math.min(xStart, xEnd),
        width: Math.max(1, Math.abs(xEnd - xStart)),
        color: seg.solidColor,
        opacity: seg.phase === 0 ? 0.25 : 0.85,
        seg,
      };
    }).filter(Boolean);
  }, [h1Segs, bandPositions]);

  if (!dailyOhlc || dailyOhlc.length === 0) {
    return (
      <div style={{padding:32, textAlign:"center", color:theme.text3, fontSize:13, background:theme.tint, borderRadius:8}}>
        Keine OHLC-Daten verfügbar.
      </div>
    );
  }

  return (
    <div>
      <div style={{display:"flex", alignItems:"center", gap:8, fontSize:11, color:theme.text3, marginBottom:8, paddingLeft:4, flexWrap:"wrap"}}>
        <span style={{fontWeight:700}}>Daily Chart:</span>
        <span>Mausrad = Zoom · Drag = Pan</span>
        <button
          onClick={() => {
            if (selectionMode) {
              setSelectionMode(false);
              setPendingStart(null);
            } else {
              setSelectionMode(true);
              setPendingStart(null);
            }
          }}
          style={{
            marginLeft: 4,
            padding: "3px 10px",
            fontSize: 10,
            fontWeight: 700,
            border: `1px solid ${selectionMode ? "#2563eb" : theme.border}`,
            borderRadius: 4,
            background: selectionMode ? "#2563eb" : theme.white,
            color: selectionMode ? "white" : theme.text2,
            cursor: "pointer",
          }}
        >
          {selectionMode
            ? (pendingStart ? "Klick 2 = Ende setzen (ESC = abbrechen)" : "Klick 1 = Start setzen (ESC = abbrechen)")
            : "▮ H1-Range wählen"}
        </button>
        <button
          onClick={() => setShowSignals(s => !s)}
          style={{
            marginLeft: 4,
            padding: "3px 10px",
            fontSize: 10,
            fontWeight: 700,
            border: `1px solid ${showSignals ? "#7c3aed" : theme.border}`,
            borderRadius: 4,
            background: showSignals ? "#7c3aed" : theme.white,
            color: showSignals ? "white" : theme.text2,
            cursor: "pointer",
          }}
        >
          {showSignals ? "✓ BoS/CHoCH" : "+ BoS/CHoCH"}
        </button>
        <button
          onClick={() => setLogScale(l => !l)}
          style={{
            marginLeft: 4,
            padding: "3px 10px",
            fontSize: 10,
            fontWeight: 700,
            border: `1px solid ${theme.border}`,
            borderRadius: 4,
            background: theme.white,
            color: theme.text2,
            cursor: "pointer",
            fontFamily: fonts?.mono,
          }}
        >
          {logScale ? "Log" : "Linear"}
        </button>
        {signalsLoading && <span style={{color:theme.blueText, fontSize:10}}>Lade…</span>}
        {showSignals && signals.length > 0 && (
          <span style={{color:theme.text4, fontSize:10}}>{signals.length} Signals</span>
        )}
        {showSignals && signalsError && (
          <span style={{color:theme.redText, fontSize:10}}>Fehler: {signalsError}</span>
        )}
        {selectedRange && (
          <span style={{marginLeft:"auto", color:theme.blueText, fontWeight:700}}>
            Auswahl: {selectedRange[0].toISOString().slice(0,10)} → {selectedRange[1].toISOString().slice(0,10)}
          </span>
        )}
      </div>

      <div style={{position:"relative", borderRadius:8, border:`1px solid ${theme.divider}`, overflow:"hidden", background:"#ffffff"}}>
        <div style={{position:"absolute", top:0, left:0, right:0, height:440, pointerEvents:"none", zIndex:1}}>
          {bandPositions.map(b => (
            <div
              key={b.idx}
              style={{
                position:"absolute",
                top:0, height:"100%",
                left: b.left, width: b.width,
                background: b.color,
              }}
            />
          ))}
        </div>

        <div ref={containerRef} style={{position:"relative", zIndex:2, width:"100%", height:440, cursor: selectionMode ? "crosshair" : "default"}}/>

        {/* Pending-Start: Vertikale Linie nach 1. Klick */}
        {pendingStartCoord !== null && (
          <div
            style={{
              position:"absolute",
              top:0, height:440,
              left: pendingStartCoord - 1, width: 2,
              background: "#2563eb",
              pointerEvents:"none",
              zIndex:3,
            }}
          />
        )}

        {selectionCoords && (
          <div
            style={{
              position:"absolute",
              top:0, height:440,
              left: selectionCoords.left, width: selectionCoords.width,
              background: "rgba(37, 99, 235, 0.15)",
              border: "1px dashed #2563eb",
              pointerEvents:"none",
              zIndex:3,
            }}
          />
        )}

        {/* BoS/CHoCH Labels mittig auf den Linien (HTML-Overlay) */}
        {signalLabels.map((lbl, i) => (
          <div
            key={`sig_${i}`}
            style={{
              position:"absolute",
              left: lbl.x,
              top: lbl.y,
              transform: "translate(-50%, -50%)",
              padding: "1px 5px",
              fontSize: 9,
              fontWeight: 700,
              fontFamily: fonts?.mono || "monospace",
              color: "white",
              background: lbl.color,
              borderRadius: 3,
              pointerEvents: "none",
              zIndex: 4,
              whiteSpace: "nowrap",
              lineHeight: 1.2,
            }}
          >
            {lbl.text}
          </div>
        ))}
      </div>

      <div style={{marginTop:4, position:"relative", height:18, borderRadius:4, overflow:"hidden", background:theme.tint, border:`1px solid ${theme.divider}`}}>
        {h1StripPositions.map(b => (
          <div
            key={`h1_${b.idx}`}
            style={{
              position:"absolute",
              top:0, height:"100%",
              left: b.left, width: b.width,
              background: b.color,
              opacity: b.opacity,
            }}
            title={`${b.seg.label} · ${b.seg.start.toISOString().slice(0,16)} → ${b.seg.end.toISOString().slice(0,16)}`}
          />
        ))}
      </div>

      <div style={{display:"flex", flexWrap:"wrap", gap:14, marginTop:8, paddingLeft:4, fontSize:10, color:theme.text3}}>
        <div style={{display:"flex", alignItems:"center", gap:4}}><span style={{display:"inline-block", width:12, height:10, background:"#16a34a"}}/> ETAB Bull</div>
        <div style={{display:"flex", alignItems:"center", gap:4}}><span style={{display:"inline-block", width:12, height:10, background:"#dc2626"}}/> ETAB Bear</div>
        <div style={{display:"flex", alignItems:"center", gap:4}}><span style={{display:"inline-block", width:12, height:10, background:"#fb923c"}}/> Pivot</div>
        <div style={{display:"flex", alignItems:"center", gap:4}}><span style={{display:"inline-block", width:12, height:10, background:"#cbd5e1"}}/> Undefined</div>
      </div>
    </div>
  );
}
